'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { logger } from '@/lib/logger';

export type CookieCategory =
  | 'essential'
  | 'analytics'
  | 'functional'
  | 'marketing';

export interface CookiePreferences {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  hasConsented: boolean;
  consentDate?: string;
  version: string;
}

interface CookieConsentContextType {
  preferences: CookiePreferences;
  showBanner: boolean;
  showPreferences: boolean;
  acceptAll: () => void;
  acceptSelected: (selectedPreferences: Partial<CookiePreferences>) => void;
  rejectAll: () => void;
  updatePreferences: (preferences: Partial<CookiePreferences>) => void;
  togglePreferences: () => void;
  hasConsent: (category: CookieCategory) => boolean;
}

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

const COOKIE_CONSENT_KEY = 'cv-matcher-cookie-consent';
const CONSENT_VERSION = '1.0.0';

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  functional: false,
  marketing: false,
  hasConsented: false,
  version: CONSENT_VERSION,
};

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        const parsedPreferences = JSON.parse(stored);

        // Check if consent version is current
        if (parsedPreferences.version === CONSENT_VERSION) {
          setPreferences(parsedPreferences);
        } else {
          // Version mismatch, show banner for new consent
          setShowBanner(true);
        }
      } else {
        // No stored preferences, show banner
        setShowBanner(true);
      }
    } catch (error) {
      logger.error('Error loading cookie preferences:', error);
      setShowBanner(true);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
      } catch (error) {
        logger.error('Error saving cookie preferences:', error);
      }
    }
  }, [preferences, isLoaded]);

  const savePreferences = (newPreferences: Partial<CookiePreferences>) => {
    const updatedPreferences: CookiePreferences = {
      ...defaultPreferences,
      ...newPreferences,
      essential: true, // Essential cookies are always enabled
      hasConsented: true,
      consentDate: new Date().toISOString(),
      version: CONSENT_VERSION,
    };

    setPreferences(updatedPreferences);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    });
  };

  const acceptSelected = (selectedPreferences: Partial<CookiePreferences>) => {
    savePreferences(selectedPreferences);
  };

  const rejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
    });
  };

  const updatePreferences = (newPreferences: Partial<CookiePreferences>) => {
    const updatedPreferences = {
      ...preferences,
      ...newPreferences,
      essential: true, // Ensure essential remains true
      hasConsented: true,
      consentDate: new Date().toISOString(),
    };
    setPreferences(updatedPreferences);
  };

  const togglePreferences = () => {
    setShowPreferences(!showPreferences);
  };

  const hasConsent = (category: CookieCategory): boolean => {
    if (category === 'essential') return true; // Essential cookies are always allowed
    return preferences[category] || false;
  };

  const value: CookieConsentContextType = {
    preferences,
    showBanner,
    showPreferences,
    acceptAll,
    acceptSelected,
    rejectAll,
    updatePreferences,
    togglePreferences,
    hasConsent,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextType {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider'
    );
  }
  return context;
}
